import { batchPostMutation } from '@/graphql/mutations/BatchPostMutation.mutation';
import { PostStatusEnum, ZnglyPostTypeEnum } from '@/graphql/types';
import { Box, Button, Checkbox, Stack, type TableCellProps, Tooltip, Typography } from '@mui/material';
import {
  type MRT_ColumnDef,
  type MRT_DisplayColumnDef,
  MRT_ExpandAllButton,
  type MRT_TableInstance,
  MRT_ToggleFullScreenButton,
  MaterialReactTable,
  createMRTColumnHelper,
  useMaterialReactTable,
} from 'material-react-table';
import { type MutableRefObject, Suspense, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { useMultiViewPickerStore } from '../MultiViewPickerStore';

import type { PostsLoaderTypesUnion } from '@/Components/posts/loaders';
import { getStatusTypeObject, toPostStatusEnum } from '@/Components/posts/status';
import { prettyVisibility } from '@/Components/posts/utils/catalog';
import type { Forms_Pagination_Fragment$data } from '@/graphql/queries/Form.query';
import {
  ReviewUsageQuery,
  type Review_Usage_Query,
  type Review_Usage_Query$data,
} from '@/graphql/queries/Review.query';
import type { AccessorFn, DeepKeys, DeepValue, GroupingState } from '@tanstack/react-table';
import { useLazyLoadQuery } from 'react-relay';
import type { DeepNonNullable, DeepNullable } from 'ts-essentials';
import type { SelectActionsConst } from './SelectActions';

export const currentPostTypeFromRoute = (): ZnglyPostTypeEnum | null => {
  const location = window.location.pathname;

  if (location.includes('dash/hubs/hubs')) {
    return ZnglyPostTypeEnum.Hub;
  }
  if (location.includes('dash/hubs/pages')) {
    return ZnglyPostTypeEnum.HubPage;
  }
  if (location.includes('dash/catalog')) {
    return ZnglyPostTypeEnum.Catalog;
  }
  if (location.includes('dash/znglists')) {
    return ZnglyPostTypeEnum.Znglist;
  }
  if (location.includes('dash/crm/forms')) {
    return ZnglyPostTypeEnum.Form;
  }
  if (location.includes('dash/crm/contacts/lists')) {
    return ZnglyPostTypeEnum.CrmList;
  }
  if (location.includes('/media')) {
    return ZnglyPostTypeEnum.MediaItem;
  }

  return null;
};

const getUrlFromPostType = (postType: ZnglyPostTypeEnum, id: string) => {
  switch (postType) {
    case ZnglyPostTypeEnum.Hub:
      return `/dash/hubs/hubs/${id}`;
    case ZnglyPostTypeEnum.HubPage:
      return `/dash/hubs/pages/${id}`;
    case ZnglyPostTypeEnum.Catalog:
      return `/dash/content/${id}`;
    case ZnglyPostTypeEnum.Znglist:
      return `/dash/znglists/${id}`;
    case ZnglyPostTypeEnum.Form:
      return `/dash/crm/forms/${id}`;
    case ZnglyPostTypeEnum.Category:
      return '/dash/content/categories';
    case ZnglyPostTypeEnum.SiteSettings:
      return '/dash/settings';
    case ZnglyPostTypeEnum.CrmList:
      return '/test/media';
    default:
      return null;
  }
};

const WithToolTip = ({ value }: { value: string }) => {
  return (
    <Tooltip title={value} placement="top" enterDelay={250}>
      <span>{value?.slice(0, 50)}...</span>
    </Tooltip>
  );
};

const WithLink = ({ children, url }: { children: React.ReactNode; url: string }) => {
  return (
    <a href={url} target="_blank" style={{ color: 'inherit' }} rel="noreferrer">
      {children}
    </a>
  );
};

const WithCheckbox = ({ id }: { id: string }) => {
  const updateNode = useMultiViewPickerStore((state) => state.updateNode);
  const node = useMultiViewPickerStore((state) => state.nodes.get(id));

  if (!node) return null;

  return (
    <Checkbox
      checked={node?.checked}
      onChange={() => {
        updateNode(id, { ...node, checked: !node?.checked });
      }}
    />
  );
};

const statusStyles = (value: string | string[]): Partial<TableCellProps> => {
  let status = '';
  if (Array.isArray(value) && value.length > 0) {
    status = String(value[0]) as string;
  }
  status = getStatusTypeObject({ status: toPostStatusEnum(status) })?.value ?? '';

  // colors to use for cell background
  const colors = {
    red: '#facfce',
    yellow: '#f5f6c3',
    green: '#d6fcd3',
    blue: '#d5dfff',
  };

  let sx = {} as TableCellProps['sx'];
  switch (status) {
    case PostStatusEnum.Publish:
      sx = (theme) => ({
        backgroundColor: colors.red,
        color: theme.palette.getContrastText(colors.red),
      });
      break;
    case PostStatusEnum.Hidden:
    case PostStatusEnum.HiddenBS:
      sx = (theme) => ({
        backgroundColor: colors.yellow,
        color: theme.palette.getContrastText(colors.yellow),
      });
      break;
    case PostStatusEnum.Draft:
      sx = (theme) => ({
        backgroundColor: colors.blue,
        color: theme.palette.getContrastText(colors.blue),
      });
      break;
  }

  return {
    sx,
  };
};

const WithStatus = ({ value }: { value: string | string[] }) => {
  if (!value) return null;

  let status = '';
  if (Array.isArray(value) && value.length > 0) {
    status = String(value?.[0]) as string;
  }

  const obj = getStatusTypeObject({ status: toPostStatusEnum(status) });
  if (!obj) return null;

  return (
    <Stack direction="row" alignItems="center">
      {obj?.icon}
      <Typography sx={{ ml: 0.5 }} variant="body2">
        {obj?.label}
      </Typography>
    </Stack>
  );
};

const getDateValue = (value?: string) => {
  if (!value) {
    return value;
  }

  const date = new Date(value);
  if (date.toString() !== 'Invalid Date') {
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    const hours = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  }

  return value ?? null;
};

type ColInputRendererReturn<
  TValue extends TAccessor extends AccessorFn<ColumnNode, infer TReturn>
    ? TReturn
    : TAccessor extends DeepKeys<ColumnNode>
      ? DeepValue<ColumnNode, TAccessor>
      : never,
  TAccessor = AccessorFn<ColumnNode> | DeepKeys<ColumnNode>,
> = Omit<MRT_DisplayColumnDef<ColumnNode, TValue>, 'header'>;

const colInuptRenderer = <
  TValue extends TAccessor extends AccessorFn<ColumnNode, infer TReturn>
    ? TReturn
    : TAccessor extends DeepKeys<ColumnNode>
      ? DeepValue<ColumnNode, TAccessor>
      : never,
  TAccessor = AccessorFn<ColumnNode> | DeepKeys<ColumnNode>,
>(
  table: MutableRefObject<MRT_TableInstance<ColumnNode> | null>,
  format: 'string' | 'date' = 'string'
): ColInputRendererReturn<TValue> => {
  return {
    enableColumnActions: false,
    Cell: ({ cell }) => {
      if (table?.current?.getState()?.grouping?.[0] === 'resKey') {
        return null;
      }
      const value = cell.getValue<string>();

      if (format === 'date') {
        return getDateValue(value);
      }

      if (value?.length > 50) {
        return <WithToolTip value={value} />;
      }
      return value ?? null;
    },
    aggregationFn: 'unique',
    AggregatedCell: ({ cell }) => {
      if (table?.current?.getState()?.grouping?.[0] === 'resKey') {
        const value = cell.getValue<string>();

        if (format === 'date') {
          return getDateValue(value);
        }

        if (value?.length > 50) {
          return <WithToolTip value={value} />;
        }
        return value ?? null;
      }
      return null;
    },
  };
};

const initDep: DeepNullable<
  DeepNonNullable<Review_Usage_Query$data['reviewUsage']>['results'][number]['dependencies'][number]
> = Object.freeze({
  created: null,
  depKey: null,
  modified: null,
  postType: null,
  status: null,
  title: null,
});

type ColumnNode = DeepNonNullable<Review_Usage_Query$data['reviewUsage']>['results'][number] & {
  node: PostsLoaderTypesUnion['paginationNodeType'];
  hasDependencies: boolean;
};

const colHelper = createMRTColumnHelper<ColumnNode>();

type ReviewUsageModalBodyProps = {
  selectedAction: (typeof SelectActionsConst)[number];
  closeModal: () => void;
  isFallback?: boolean;
  data?: DeepNonNullable<Review_Usage_Query$data>;
};
const ReviewUsageModalBody = (props: ReviewUsageModalBodyProps & { selectedIds: string[] }) => {
  const count = useMultiViewPickerStore((state) => state.selectedNodesCount);
  const getStoreNode = useMultiViewPickerStore((state) => state.getNode);
  const updateNode = useMultiViewPickerStore((state) => state.updateNode);
  const getSelectedNodes = useMultiViewPickerStore((state) => state.getSelectedNodes);
  const postTypeFromRoute = useMemo(() => currentPostTypeFromRoute(), []);
  const [grouping, setGrouping] = useState<GroupingState>(['resKey']);

  const tableRef = useRef<MRT_TableInstance<ColumnNode> | null>(null);

  const { selectedTotalDeps, selectedWithDeps } = useMemo(() => {
    let selectedWithDeps = 0;
    let selectedTotalDeps = 0;
    if (!props?.data?.reviewUsage?.results?.length) {
      return {
        selectedWithDeps,
        selectedTotalDeps,
      };
    }

    for (const result of props.data.reviewUsage.results) {
      const isSelected = getStoreNode(result.resKey)?.checked;
      if (isSelected && result.dependencies?.length > 0) {
        selectedWithDeps++;
        selectedTotalDeps += result.dependencies.length;
      }
    }

    return {
      selectedWithDeps,
      selectedTotalDeps,
    };
  }, [count, props?.data?.reviewUsage?.results, getStoreNode, getSelectedNodes()]);

  const columns: MRT_ColumnDef<ColumnNode>[] = useMemo(
    () => [
      // post type
      colHelper.group({
        header: postTypeFromRoute ?? '',
        Header(props) {
          // hide the status header column
          if (props.header.id === `1_${postTypeFromRoute}_node.status`) {
            return <></>;
          }

          return <span>{postTypeFromRoute}</span>;
        },
        muiTableHeadCellProps(props) {
          if (props.table.getState()?.grouping?.[0] === 'Usage') {
            return {
              sx: {
                borderLeft: '1px solid blue',
              },
            };
          }
          return {};
        },
        columns: [
          colHelper.accessor('resKey', {
            header: 'Title',
            muiTableHeadCellProps: (props) => ({
              sx: {
                borderLeft: props.table.getState().grouping?.[0] === 'Usage' ? '1px solid' : '',
              },
            }),
            muiTableBodyCellProps: (props) => ({
              sx: {
                borderLeft: props.table.getState().grouping?.[0] === 'Usage' ? '1px solid' : '',
              },
            }),
            Header: () => {
              const selectAll = () => {
                props?.selectedIds.forEach((id) => {
                  const nodeFromStore = getStoreNode(id);
                  if (!!nodeFromStore) {
                    updateNode(id, { ...nodeFromStore, checked: true });
                  }
                });
              };

              const deselectAll = () => {
                // we should only deselect the nodes with dependencies -> these are shown in  our table
                props?.selectedIds.forEach((id) => {
                  try {
                    // check if the node exists in the table
                    const matchedRow = table?.getRow?.(id, false);
                    if (!!matchedRow && matchedRow?.original?.hasDependencies) {
                      const nodeFromStore = getStoreNode(id);
                      if (!!nodeFromStore) {
                        updateNode(id, { ...nodeFromStore, checked: false });
                      }
                    }
                  } catch (e) {
                    //
                  }
                });
              };

              const isSelectAll = props?.selectedIds.every((id) => getStoreNode(id)?.checked);

              return (
                <Box>
                  <Checkbox checked={isSelectAll} onChange={isSelectAll ? deselectAll : selectAll} />
                  <span>Title</span>
                </Box>
              );
            },
            Cell: ({ row }) => {
              let value = (
                row.original?.node as PostsLoaderTypesUnion['paginationNodeType'] & { title?: string | null }
              )?.title;
              if (postTypeFromRoute === ZnglyPostTypeEnum.Form) {
                value = (
                  row.original?.node as NonNullable<Forms_Pagination_Fragment$data['forms']>['edges'][number]['node']
                )?.content;
              }
              if (value?.length && value?.length > 50) {
                return (
                  <>
                    <WithCheckbox id={row.original.resKey} />
                    <WithToolTip value={value} />
                  </>
                );
              }

              return (
                <>
                  <WithCheckbox id={row.original.resKey} />
                  {value ?? null}
                </>
              );
            },
            GroupedCell: ({ row }) => {
              let value = (
                row.original?.node as PostsLoaderTypesUnion['paginationNodeType'] & { title?: string | null }
              )?.title;
              if (postTypeFromRoute === ZnglyPostTypeEnum.Form) {
                value = (
                  row.original?.node as NonNullable<Forms_Pagination_Fragment$data['forms']>['edges'][number]['node']
                )?.content;
              }
              if (value?.length && value?.length > 50) {
                return (
                  <>
                    <WithCheckbox id={row.original.resKey} />
                    <WithToolTip value={value} />
                  </>
                );
              }

              return (
                <>
                  <WithCheckbox id={row.original.resKey} />
                  {value ?? null}
                </>
              );
            },
          }),
          colHelper.accessor('node.status', {
            header: 'Status',
            enableColumnActions: false,
            Cell: ({ cell }) => {
              if (tableRef.current?.getState?.()?.grouping?.[0] === 'resKey') {
                return null;
              }
              return <WithStatus value={cell.getValue<string>()} />;
            },
            muiTableBodyCellProps: (props) => {
              if (!props.cell.getIsAggregated()) {
                return {};
              }
              return {
                ...statusStyles(props.cell.getValue<string>()),
              };
            },
            aggregationFn: 'unique',
            AggregatedCell: ({ cell }) => {
              if (tableRef.current?.getState?.()?.grouping?.[0] === 'resKey') {
                return <WithStatus value={cell.getValue<string>()} />;
              }
              return null;
            },
          }),
          colHelper.accessor('node.author.node.name', {
            header: 'Author',
            ...colInuptRenderer(tableRef),
          }),
          // @ts-ignore
          ...(postTypeFromRoute === ZnglyPostTypeEnum.Catalog
            ? [
                // @ts-ignore
                colHelper.accessor('node.znglyContent.contentMediaName', {
                  header: 'Media',
                  ...colInuptRenderer(tableRef),
                }),
                colHelper.accessor('node.date', {
                  header: 'Date',
                  ...colInuptRenderer(tableRef, 'date'),
                }),
              ]
            : []),
          // @ts-ignore
          ...(postTypeFromRoute === ZnglyPostTypeEnum.Znglist
            ? [
                colHelper.accessor('node.date', {
                  header: 'Date',
                  ...colInuptRenderer(tableRef, 'date'),
                }),
              ]
            : []),
          // @ts-ignore
          ...(postTypeFromRoute === ZnglyPostTypeEnum.Form
            ? [
                colHelper.accessor('node.title', {
                  header: 'Description',
                  ...colInuptRenderer(tableRef),
                }) as MRT_ColumnDef<ColumnNode>,
                colHelper.accessor('node.created', {
                  header: 'Created',
                  ...colInuptRenderer(tableRef, 'date'),
                }),
                colHelper.accessor('node.modified', {
                  header: 'Modified',
                  ...colInuptRenderer(tableRef, 'date'),
                }),
              ]
            : []),
          // @ts-ignore
          ...(postTypeFromRoute === ZnglyPostTypeEnum.Hub
            ? [
                colHelper.accessor('node.description', {
                  header: 'Description',
                  ...colInuptRenderer(tableRef),
                }),
                colHelper.accessor('node.created', {
                  header: 'Created',
                  ...colInuptRenderer(tableRef, 'date'),
                }),
                colHelper.accessor('node.modified', {
                  header: 'Modified',
                  ...colInuptRenderer(tableRef, 'date'),
                }),
              ]
            : []),
        ],
      }),

      // Dependencies
      colHelper.group({
        header: 'Dependencies',
        muiTableHeadCellProps: {
          sx: {
            borderLeft: '1px solid',
          },
        },
        columns: [
          colHelper.accessor((row) => row.dependencies?.[0]?.postType, {
            header: 'Usage',
            muiTableHeadCellProps: {
              sx: {
                borderLeft: '1px solid',
              },
            },
            muiTableBodyCellProps: {
              sx: {
                borderLeft: '1px solid #c7c7c7',
              },
            },
          }),
          colHelper.accessor((row) => row.dependencies?.[0]?.title, {
            header: 'Title',
            enableColumnActions: false,
            muiTableHeadCellProps: (props) => ({
              sx: {
                borderLeft: props.table.getState().grouping?.[0] === 'Usage' ? '1px solid' : '',
              },
            }),
            muiTableBodyCellProps: (props) => ({
              sx: {
                borderLeft: props.table.getState().grouping?.[0] === 'Usage' ? '1px solid' : '',
              },
            }),
            Cell: ({ row, cell }) => {
              const postType = row.original.dependencies[0].postType as ZnglyPostTypeEnum;
              const id = row.original.dependencies[0].depKey;
              const url = getUrlFromPostType(postType, id);
              const value = cell.getValue<string>();

              if (value?.length > 50) {
                if (!url) {
                  return <WithToolTip value={value} />;
                }

                return (
                  <WithLink url={url}>
                    <WithToolTip value={value} />
                  </WithLink>
                );
              }

              if (!!url) {
                return <WithLink url={url}>{value}</WithLink>;
              }

              return value;
            },
          }),
          colHelper.accessor((row) => row.dependencies?.[0]?.status, {
            header: 'Status',
            enableColumnActions: false,
            Cell: ({ cell }) => {
              return <WithStatus value={cell.getValue<string>()} />;
            },
            muiTableBodyCellProps: (props) => ({
              ...statusStyles(props.cell.getValue<string>()),
            }),
          }),
          colHelper.accessor((row) => row.dependencies?.[0]?.created, {
            header: 'Created',
            enableColumnActions: false,
            Cell: ({ cell }) => {
              return getDateValue(cell.getValue<string>());
            },
          }),
          colHelper.accessor((row) => row.dependencies?.[0]?.modified, {
            header: 'Modified',
            enableColumnActions: false,
            Cell: ({ cell }) => {
              return getDateValue(cell.getValue<string>());
            },
          }),
        ],
      }),
    ],
    [postTypeFromRoute, props?.selectedIds, updateNode, tableRef]
  );

  const { data, dataDepsCount } = useMemo(() => {
    if (!props?.data?.reviewUsage?.results?.length) {
      return {
        data: [],
        dataDepsCount: 0,
      };
    }

    const results = props?.data?.reviewUsage?.results;

    let dataDepsCount = 0; // count the amount of input dependencies
    let deps = [] as ColumnNode[];
    for (const result of results) {
      const node = getStoreNode(result.resKey)?.node;
      if (!node) {
        continue;
      }

      if (!result.dependencies?.length) {
        deps.push({
          ...result,
          node,
          dependencies: [initDep as (typeof result.dependencies)[number]],
          hasDependencies: false,
        });
        continue;
      }

      if (result.dependencies?.length > 0) {
        dataDepsCount++;
      }

      for (const dep of result.dependencies) {
        deps.push({
          resKey: result.resKey,
          node: node,
          dependencies: [dep],
          hasDependencies: true,
        });
      }
    }

    // prioritizes items with dependencies to be displayed first
    // ensures that 'a' (with dependencies) always appears before b (without dependencies) and vice versa
    deps = deps.sort((a, b) => {
      // Prioritize assets with dependencies
      if (a.hasDependencies && !b.hasDependencies) {
        return -1; // a (with dependencies) should come before b
      }
      if (!a.hasDependencies && b.hasDependencies) {
        return 1; // b (with dependencies) should come before a
      }

      // If both have dependencies
      if (a.hasDependencies && b.hasDependencies) {
        const aNode = a?.node as PostsLoaderTypesUnion['paginationNodeType'] & {
          date?: string;
          created?: string;
          modified?: string;
        };
        const bNode = b?.node as PostsLoaderTypesUnion['paginationNodeType'] & {
          date?: string;
          created?: string;
          modified?: string;
        };
        const aDate = new Date(aNode?.date ?? aNode?.created ?? aNode?.modified ?? '');
        const bDate = new Date(bNode?.date ?? bNode?.created ?? bNode?.modified ?? '');

        // sort by date (newest first)
        if (aDate > bDate) {
          return -1;
        }
        if (aDate < bDate) {
          return 1;
        }
        return 0;
      }

      // If neither a or b have dependencies
      return 0; // No change in order
    });

    return {
      data: deps,
      dataDepsCount,
    };
  }, [props?.data, getStoreNode]);

  const table = useMaterialReactTable({
    columns,
    data,
    getRowId: (row) => row.resKey,
    enableStickyHeader: true,
    enableFilters: false,
    enableSorting: false,
    enableDensityToggle: false,
    enablePagination: false,
    enableGrouping: dataDepsCount !== 0,
    enableHiding: false,
    enableColumnDragging: false,
    groupedColumnMode: 'reorder',
    enableStickyFooter: true,
    state: {
      grouping: dataDepsCount !== 0 ? grouping : [],
      isLoading: props.isFallback,
      showAlertBanner: false,
    },
    renderBottomToolbar() {
      return (
        <Stack direction="row" justifyContent="space-between" alignItems="center" p={1}>
          <Stack direction="row" alignItems="center">
            <span>
              {selectedWithDeps} of {props?.selectedIds?.length} {postTypeFromRoute?.toLowerCase()} item(s) With{' '}
              {selectedTotalDeps} Dependencies
            </span>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button onClick={executeAction} className="z-btn-1" disabled={props?.isFallback}>
              Confirm {prettyVisibility(props.selectedAction[0])}
            </Button>
            <Button onClick={props.closeModal} className="z-btn-1">
              Cancel
            </Button>
          </Stack>
        </Stack>
      );
    },
    renderTopToolbar: (tbProps) => {
      return (
        <Stack direction="row" p={1} justifyContent="space-between" alignItems="center">
          <Stack direction="row" />
          <Stack direction="column" alignItems="center" flexGrow={1}>
            <Typography variant="body1">
              Are you sure you want to move {count} item(s) to {prettyVisibility(props.selectedAction[0])}?
            </Typography>
            <Typography variant="caption">
              {props?.isFallback
                ? 'Loading dependencies...'
                : selectedWithDeps === 0
                  ? `No dependencies found. You may continue to ${props.selectedAction[1].toLowerCase()}`
                  : `${selectedWithDeps} of ${
                      props?.selectedIds?.length
                    } ${postTypeFromRoute?.toLowerCase()} item(s) are being used in other places. Please review before proceeding.`}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center">
            <MRT_ToggleFullScreenButton {...tbProps} />
          </Stack>
        </Stack>
      );
    },
    muiTableBodyRowProps: (props) => {
      const disabled = props.row.original.hasDependencies === false;

      return {
        sx: {
          backgroundColor: disabled ? '#f5f5f5' : 'inherit',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
        },
      };
    },
    muiTableContainerProps: (props) => ({
      style: {
        height: props.table.getState().isFullScreen ? 'calc(100vh - 120px)' : 'unset', // height - (header + footer)
      },
      sx: {
        background: 'white',
        borderRadius: props.table.getState().isFullScreen ? 'unset' : '8px',
        maxWidth: '100%',
        maxHeight: '500px',
        minHeight: '300px',
      },
    }),
    onGroupingChange: (updater) => {
      if (typeof updater !== 'function') {
        return;
      }
      // only can have one grouping at a time
      const newGroup = updater([]);
      if (newGroup.length === 0) {
        return;
      }

      setGrouping(newGroup);
    },
    muiTablePaperProps: (props) => ({
      sx: {
        maxWidth: props.table.getState().isFullScreen ? 'unset' : 'calc(100% - 32px)',
        p: 1,
      },
    }),
    muiTableHeadProps: {
      sx: {
        // set the second th to be 39px from the top -> since we have two different header rows, allows us to have sticky headers
        'tr:nth-child(2)': {
          top: '39px !important',
          '& th': {
            backgroundColor: '#2d2d2d',
            color: 'white',
            borderLeftColor: '#c7c7c7 !important',
            '& button': {
              color: 'white',
            },
          },
        },
        'tr:nth-child(1) th': {
          backgroundColor: 'white !important',
          borderLeftColor: '#c7c7c7 !important',
        },
      },
    },
    displayColumnDefOptions: {
      'mrt-row-expand': {
        Header(props) {
          return (
            <Stack direction="row">
              <Typography variant="body2" fontWeight="bold" mt="2px" fontSize="100%">
                {props?.table?.getIsAllRowsExpanded() ? 'Collapse All' : 'Expand All'}
              </Typography>
              <MRT_ExpandAllButton {...props} />
            </Stack>
          );
        },
      },
    },
  });

  const executeAction = () => {
    const postType = currentPostTypeFromRoute();
    if (!postType) {
      return;
    }

    const postIds = useMultiViewPickerStore.getState().getSelectedNodes();

    let newStatus = props.selectedAction[0];
    if (postType === ZnglyPostTypeEnum.MediaItem && props.selectedAction[0] === PostStatusEnum.Publish) {
      newStatus = PostStatusEnum.Inherit;
    }

    batchPostMutation(
      {
        input: {
          ids: postIds,
          status: newStatus,
          postType,
        },
      },
      {
        onCompleted: (data, errors) => {
          if (!!errors?.length) {
            toast.error(
              `Error moving to ${props.selectedAction[1].toLowerCase()}: ${errors?.map((e) => e?.message).join(', ')}`
            );
          }

          if (data?.batchPostMutation?.ids?.length) {
            toast.success(
              `Successfully moved ${data.batchPostMutation.ids.length} to ${prettyVisibility(props.selectedAction[0])}`
            );
          }
          props.closeModal();
        },
      }
    );
  };

  useEffect(() => {
    tableRef.current = table;
  }, []);

  return <MaterialReactTable table={table} />;
};

const DataLoader = (props: ReviewUsageModalBodyProps) => {
  // we only want this value once when the component is mounted
  const selectedNodes = useMemo(() => {
    return useMultiViewPickerStore.getState().getSelectedNodes();
  }, []);

  const data = useLazyLoadQuery<Review_Usage_Query>(
    ReviewUsageQuery,
    {
      input: {
        postIds: selectedNodes,
        postType: currentPostTypeFromRoute()!,
      },
    },
    { fetchPolicy: 'store-and-network' }
  );

  return (
    <ReviewUsageModalBody
      {...props}
      data={data as DeepNonNullable<Review_Usage_Query$data>}
      selectedIds={selectedNodes}
    />
  );
};

/**
 * Wrapper for EditHub
 * Get the hub id from the url and Provide Suspense for fallback until the data is loaded
 */
const ModalWrapper = (props: ReviewUsageModalBodyProps) => {
  return (
    <Suspense fallback={<ReviewUsageModalBody {...props} isFallback selectedIds={[]} />}>
      <DataLoader {...props} />
    </Suspense>
  );
};

const SelectActionReviewUsage = ModalWrapper;

export default SelectActionReviewUsage;
